import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import './Home.css';

const Reveal = ({ children, delay = 0, className = '', direction = 'up' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const variants = {
    up: { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } },
    left: { hidden: { opacity: 0, x: -32 }, visible: { opacity: 1, x: 0 } },
    right: { hidden: { opacity: 0, x: 32 }, visible: { opacity: 1, x: 0 } },
    scale: { hidden: { opacity: 0, scale: 0.94 }, visible: { opacity: 1, scale: 1 } },
  };
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={variants[direction]}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Home = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [newsletterDone, setNewsletterDone] = useState(false);
  const [contactDone, setContactDone] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  const ventures = [
    {
      id: 'digital-services',
      name: 'Digital & Campaign Services',
      tag: 'Corporate Branding & Media Services',
      description: "Full-service political and brand campaign management. Built for Maharashtra's electoral and commercial landscape — precision, reach, and regional intelligence.",
      color: '#B86B4B',
    },
    {
      id: 'eco-warriors',
      name: 'Eco-Warriors\u2122',
      tag: 'EdTech & Impact Gaming',
      description: "Africa's first UNESCO-patronaged educational game. Deployed across 320+ schools in 4 countries, reaching 89,000+ students. Teaches recycling and waste management through gamification — and turns that data into carbon reduction intelligence for manufacturers.",
      color: '#4A7C59',
    },
    {
      id: 'discover-solapur',
      name: 'Discover Solapur\u2122',
      tag: 'Technology & Culture',
      description: 'A platform built to surface local culture, events, and experiences in Tier 2 India. The layer of discovery that cities like Solapur have always deserved.',
      color: '#2D5A7B',
    },
    {
      id: 'prismatex',
      name: 'PrismaTex',
      tag: 'Sustainable Manufacturing',
      description: "A 20,000 sq ft manufacturing facility in Kumbhari, Solapur. Established with \u20B92.62 Crore NCDC funding. Employs 280 women members. Prisma's commitment to community-first infrastructure in Tier 2 India.",
      color: '#8E5A3C',
    },
    {
      id: 'prisma-terra',
      name: 'Prisma Terra',
      tag: 'Real Estate & Infrastructure',
      description: 'NA plot development and real estate operations in Solapur. Long-horizon infrastructure investments with community-first thinking.',
      color: '#C4A484',
    },
    {
      id: 'prisma-studio',
      name: 'Prisma Studio',
      tag: 'Strategic Execution Layer',
      description: 'The internal systems behind every Prisma venture, selectively opened to external partners. Branding architecture, operational systems, and strategic advisory — not an agency, a precision layer.',
      color: '#7B5EA7',
    },
  ];

  const principles = [
    { title: 'Architecture over hustle', body: 'Every venture is designed for scale from day one, not retrofitted later.' },
    { title: 'Depth over breadth', body: 'We operate in sectors where cultural understanding is the competitive advantage.' },
    { title: 'Tier 2 as origin, not limitation', body: 'Solapur is not a constraint. It is where authenticity lives.' },
  ];

  const modelSteps = [
    { num: '01', label: 'Identify', body: 'Find underserved sectors where cultural context creates durable advantage.' },
    { num: '02', label: 'Build', body: 'Launch ventures with systems thinking, not just product thinking.' },
    { num: '03', label: 'Scale', body: 'Connect ventures to amplify impact across sectors.' },
  ];

  const proofMetrics = [
    { value: '4+', label: 'Countries' },
    { value: '\u20B92.62Cr+', label: 'Funded' },
    { value: '89,000+', label: 'Students Reached' },
    { value: '5', label: 'Active Ventures' },
  ];

  const caseSnapshots = [
    { name: 'Eco-Warriors\u2122', body: "Africa's first UNESCO-patronaged educational mobile game. Deployed across 320+ schools in 4 African countries, reaching 89,000+ students with 39,000+ tablets distributed. Teaches recycling and waste management through gamification — built with government education departments." },
    { name: 'Digital & Campaign Services', body: "Maharashtra's full-service political and brand campaign management arm — combining digital precision with on-ground regional intelligence across political and commercial briefs." },
    { name: 'Discover Solapur\u2122', body: 'A cultural tourism and discovery platform built to surface local culture, events, and experiences in Tier 2 India. The layer of discovery that cities like Solapur have always deserved.' },
  ];

  const journey = [
    { year: '2025', title: 'Founded The Prisma Global & NCDC Funding', body: 'Established a registered impact-driven startup in Solapur, Maharashtra. Integrated learnings from previous ventures into a multi-vertical platform model. Secured \u20B92.62 Crore NCDC funding for PrismaTex, establishing a 20,000 sq ft manufacturing facility in Kumbhari with 280 women members.', tags: ['4 Divisions Launched', '\u20B92.62Cr Funding', '280 Women Employed'] },
    { year: '2024', title: 'Electoral Campaign Management: Hon. Radhakrishna Vikhe Patil', body: "Led comprehensive social media and digital strategy for Hon. Radhakrishna Vikhe Patil's Maharashtra Vidhan Sabha elections. Managed state-wide campaign with a team of 50+ content creators.", tags: ['Campaign Management', 'State-Wide Strategy', '50+ Team Members'] },
    { year: '2023', title: 'Electoral Campaign Management: Ahmednagar', body: "Managed comprehensive social media and digital strategy for Sujay Vikhe Patil's Ahmednagar constituency campaign. Led a team of 50+ content creators with state-wide reach.", tags: ['Campaign Manager', '50+ Team Members', 'State-Wide Campaign'] },
    { year: '2022', title: 'Exit from Panda & Wolf Holdings', body: 'Successfully exited Panda & Wolf Holdings after 8 years of operations. Transitioned leadership and returned to India to focus on impact-driven ventures in Maharashtra.', tags: ['Strategic Exit', '8-Year Journey', 'Return to India'] },
    { year: '2022', title: "Africa's Women Leader — World Women Leadership Congress", body: "Recognised for outstanding leadership and impact in driving innovation across Africa's technology and entrepreneurship ecosystem.", tags: ['World Women Leadership Congress', "Africa's Women Leader"] },
    { year: '2020\u201321', title: 'Eco-Warriors\u2122: National Curriculum Integration', body: 'Achieved full curriculum integration across 320+ schools in 4 African countries. Game reached 89,000+ students with 39,000+ tablets distributed and Ministry of Education partnerships.', tags: ['320+ Schools', '89K+ Students', 'Ministry Partnership'] },
    { year: '2020', title: 'Best Sustainable EdTech Award — Women in Tech Africa, Mauritius Chapter', body: "Awarded for Eco-Warriors\u2122' innovative approach to environmental education and sustainable development through gaming.", tags: ['Women in Tech Africa', 'Best Sustainable EdTech'] },
    { year: '2019', title: "Digital Women's Award — She The People & Google India", body: "Recognised for championing women's empowerment through technology and digital innovation in underserved communities.", tags: ["Digital Women's Award", 'She The People', 'Google India'] },
    { year: '2019', title: 'Female Role Model of the Year — Global Startup Awards', body: 'Recognised for exemplary leadership in building scalable technology ventures with social impact across emerging markets.', tags: ['Global Startup Awards', 'Female Role Model'] },
    { year: '2019', title: 'Eco-Warriors\u2122 Launch & Global Recognition', body: "Launched Africa's first UNESCO-patronaged educational game, teaching children about recycling and waste management through gamification. Won Best Social Impact Startup at the Southern Africa Awards. Featured in international media across 5 countries.", tags: ['UNESCO Patronage', 'Best Social Impact', 'International Coverage'] },
    { year: '2018', title: 'Discover Mauritius™: Peak Scale', body: 'Scaled the Discover Mauritius™ cultural tourism platform to 300,000+ users across iOS, Android and PWA. Generated $150K+ in revenue. Built sophisticated data visualisation dashboards and a B2B SaaS model.', tags: ['300K+ Users', '$150K+ Revenue', '3 Platforms'] },
    { year: '2017', title: 'Panda & Wolf Expansion to Mauritius', body: 'Expanded operations to Mauritius, establishing a regional headquarters and launching cultural tourism and EdTech initiatives across the Indian Ocean region.', tags: ['International Expansion', 'Regional HQ', 'Market Entry'] },
    { year: '2014', title: 'Founded Panda & Wolf Holdings, Pune', body: 'Co-founded Panda & Wolf Holdings in Pune as a creative technology company. Built the foundation for future ventures in EdTech and cultural tourism.', tags: ['Company Founded', 'Pune Operations', 'Creative Tech'] },
  ];

  const studioServices = ['Brand Systems', 'Operations Architecture', 'Strategic Advisory'];

  const pressHighlights = [
    {
      source: 'UNESCO',
      headline: 'Eco-Warriors\u2122 — The first ever Ecological game to be launched under UNESCO\u2019s patronage across Africa',
      link: 'https://www.unesco.org/en/articles/eco-warriorstm-first-ever-ecological-game-be-launched-under-unescos-patronage-across-africa',
    },
    {
      source: 'YourStory',
      headline: 'How this 26-year-old woman from Solapur put Mauritius on the global map with UNESCO-backed Eco-Warriors',
      link: 'https://yourstory.com/herstory/2019/12/startup-pune-solapur-panda-wolf-eco-warriors-unesco-ecological-game',
    },
    {
      source: 'Frenchweb.fr',
      headline: '[Made in Oc\u00e9an Indien] Veda Dean, de la blouse blanche \u00e0 la startup, il n\u2019y a eu qu\u2019un pas\u00a0!',
      link: 'https://www.frenchweb.fr/made-in-ocean-indien-veda-dean-de-la-blouse-blanche-a-la-startup-il-ny-a-eu-quun-pas/338585',
    },
  ];

  const handleContact = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      name: fd.get('name'),
      email: fd.get('email'),
      phone: '',
      userType: 'General',
      ventureInterest: fd.get('reason') || 'General',
      message: fd.get('message'),
      consent: true,
    };
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setContactDone(true);
      e.target.reset();
    } catch {
      toast({ title: 'Could not send', description: 'Please try again or email hello@theprismaglobal.com', variant: 'destructive' });
    }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fd.get('email'), source: 'homepage_inline' }),
      });
      if (!res.ok) throw new Error();
      setNewsletterDone(true);
      e.target.reset();
    } catch {
      toast({ title: 'Could not subscribe', description: 'Please try again.', variant: 'destructive' });
    }
  };

  const handleReportClick = () => {
    setTooltipVisible(true);
    setTimeout(() => setTooltipVisible(false), 2800);
  };

  return (
    <div className="prisma-home" data-testid="home-page">
      <SEO
        title="The Prisma Global | Where Light Becomes Legacy"
        description="A founder-led multi-venture ecosystem from Solapur, India — building across culture, commerce, sustainable manufacturing, and real estate."
        canonical="/"
      />
      <Navbar />

      {/* 1. HERO */}
      <section ref={heroRef} className="prisma-hero" data-testid="hero-section">
        <div className="hero-noise" />
        <svg className="hero-prism" viewBox="0 0 800 600" aria-hidden="true">
          <line x1="100" y1="500" x2="700" y2="100" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <line x1="100" y1="500" x2="700" y2="500" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <line x1="700" y1="100" x2="700" y2="500" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <line x1="200" y1="450" x2="600" y2="150" stroke="rgba(184,107,75,0.10)" strokeWidth="1" />
          <line x1="300" y1="400" x2="500" y2="200" stroke="rgba(196,164,132,0.06)" strokeWidth="1" />
        </svg>

        <motion.div className="hero-content" style={{ opacity: heroOpacity, y: heroY }}>
          <motion.span
            className="hero-eyebrow"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 56 }}
            transition={{ duration: 1.1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            aria-hidden="true"
          />
          <motion.h1
            className="hero-headline"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            Where <em className="hero-headline-em">Light</em><br/>Becomes <span className="hero-headline-shine">Legacy</span>.
          </motion.h1>
          <motion.p
            className="hero-sub"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
          >
            A multi-venture ecosystem built from Solapur &mdash; across culture, commerce, and infrastructure.
          </motion.p>
          <motion.div
            className="hero-ctas"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <button className="cta-primary" data-testid="hero-explore-ventures-btn" onClick={() => document.getElementById('ventures')?.scrollIntoView({ behavior: 'smooth' })}>Explore Ventures</button>
            <button className="cta-secondary" data-testid="hero-read-report-btn" onClick={() => document.getElementById('impact-report-cta')?.scrollIntoView({ behavior: 'smooth' })}>Read the Impact Report</button>
          </motion.div>
          <motion.div
            className="hero-trust-badges"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.45 }}
            data-testid="hero-trust-badges"
          >
            <span className="hero-trust-badge" data-testid="trust-badge-unesco">
              <span className="hero-trust-dot" />
              UNESCO-Aligned Initiative
            </span>
            <span className="hero-trust-divider" aria-hidden="true" />
            <span className="hero-trust-badge" data-testid="trust-badge-ncdc">
              <span className="hero-trust-dot" />
              Backed by NCDC &mdash; &#8377;2.62 Cr
            </span>
          </motion.div>
          <motion.button
            className="hero-press-link"
            data-testid="hero-press-link"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.7 }}
            onClick={() => document.querySelector('[data-testid="press-highlights-section"]')?.scrollIntoView({ behavior: 'smooth' })}
            type="button"
          >
            Read what UNESCO said <span className="hero-press-arrow" aria-hidden="true">&rarr;</span>
          </motion.button>
          <motion.div
            className="hero-scroll"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ duration: 1, delay: 1.8 }}
          >
            <span className="hero-scroll-dot" />
          </motion.div>
        </motion.div>
      </section>

      {/* 2. AS SEEN IN */}
      <section className="prisma-seen-in section-light" data-testid="seen-in-section">
        <div className="section-inner">
          <Reveal>
            <p className="seen-in-label">As Seen In</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="seen-in-marquee" data-testid="seen-in-row" aria-label="Press publications">
              <div className="seen-in-track">
                {[...Array(2)].map((_, dup) => (
                  <div className="seen-in-row" key={dup} aria-hidden={dup === 1}>
                    {[
                      { name: 'UNESCO', src: '/press-logos/unesco.png' },
                      { name: 'JICA', src: '/press-logos/jica.png' },
                      { name: 'YourStory', src: '/press-logos/yourstory.png' },
                      { name: 'Dainik Bhaskar', src: '/press-logos/dainik-bhaskar.png' },
                      { name: 'Sakal', src: '/press-logos/sakal.png' },
                      { name: 'The New Indian Express', src: '/press-logos/new-indian-express.png' },
                    ].map(({ name, src }) => (
                      <span
                        key={`${dup}-${name}`}
                        className="seen-in-logo-wrap"
                        data-testid={dup === 0 ? `seen-in-${name.toLowerCase().replace(/\s/g, '-')}` : undefined}
                      >
                        <img
                          src={src}
                          alt={name}
                          className="seen-in-logo-img"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling.style.display = 'inline-block';
                          }}
                        />
                        <span className="seen-in-item">{name}</span>
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3. PHILOSOPHY */}
      <section className="prisma-philosophy" data-testid="philosophy-section">
        <div className="section-inner philosophy-grid">
          <Reveal direction="left">
            <h2 className="philosophy-quote">
              &ldquo;We don&rsquo;t build companies. We build systems that outlast the moment.&rdquo;
            </h2>
          </Reveal>
          <div className="philosophy-principles">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={0.15 + i * 0.1} direction="right">
                <div className="principle-item" data-testid={`principle-${i}`}>
                  <span className="principle-line" />
                  <h3 className="principle-title">{p.title}</h3>
                  <p className="principle-body">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. PRISMA MODEL */}
      <section className="prisma-model section-light" data-testid="model-section">
        <div className="section-inner">
          <Reveal>
            <h2 className="model-heading">The Prisma Model</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="model-sub">One ecosystem. Five ventures. Each one a distinct expression of the same architectural intent.</p>
          </Reveal>
          <div className="model-steps">
            {modelSteps.map((s, i) => (
              <Reveal key={s.num} delay={0.2 + i * 0.1}>
                <div className="model-step" data-testid={`model-step-${i}`}>
                  <span className="model-num">{s.num}</span>
                  <h3 className="model-label">{s.label}</h3>
                  <p className="model-body">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. VENTURES */}
      <section className="prisma-ventures" id="ventures" data-testid="ventures-section">
        <div className="section-inner">
          <Reveal>
            <p className="section-label">Ventures</p>
            <h2 className="section-heading">Five expressions of one architecture.</h2>
          </Reveal>
          <div className="ventures-grid">
            {ventures.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.08} direction="scale">
                <div className="venture-card" data-testid={`venture-card-${v.id}`} style={{ '--venture-accent': v.color }}>
                  <div className="venture-card-inner">
                    <span className="venture-tag">{v.tag}</span>
                    <h3 className="venture-name">{v.name}</h3>
                    <p className="venture-desc">{v.description}</p>
                    <span className="venture-cta">Explore <span className="cta-arrow">&rarr;</span></span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. WHY PRISMA */}
      <section className="prisma-why section-light" data-testid="why-section">
        <div className="section-inner">
          <Reveal>
            <h2 className="why-quote">
              Most ecosystems are built at the center. Ours was built at the edge &mdash; where things are harder, and the work is more real.
            </h2>
          </Reveal>
          <div className="why-metrics">
            {proofMetrics.map((m, i) => (
              <Reveal key={m.label} delay={0.1 + i * 0.08}>
                <div className="why-metric" data-testid={`why-metric-${i}`}>
                  <span className="why-metric-value">{m.value}</span>
                  <span className="why-metric-label">{m.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7. PROOF OF WORK */}
      <section className="prisma-proof" data-testid="proof-section">
        <div className="section-inner">
          <Reveal>
            <h2 className="proof-heading">What Prisma Has Built</h2>
          </Reveal>
          <div className="proof-cases">
            {caseSnapshots.map((c, i) => (
              <Reveal key={c.name} delay={0.15 + i * 0.1} direction="scale">
                <article className="proof-card" data-testid={`proof-case-${i}`}>
                  <h3 className="proof-card-name">{c.name}</h3>
                  <p className="proof-card-body">{c.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 8. FOUNDER */}
      <section className="prisma-founder section-light" data-testid="founder-section">
        <div className="section-inner founder-layout">
          <Reveal direction="left">
            <div className="founder-portrait" data-testid="founder-portrait">
              <img
                src="/team/vedavati-founder.jpg"
                alt="Veda Lolge — Founder & CEO, The Prisma Global"
                className="founder-portrait-img"
              />
            </div>
          </Reveal>
          <div className="founder-copy">
            <Reveal direction="right">
              <p className="founder-eyebrow">The Founder</p>
              <h2 className="founder-name">Veda Lolge</h2>
            </Reveal>
            <Reveal delay={0.1} direction="right">
              <div className="founder-body">
                <p>Veda Lolge is a serial entrepreneur with proven expertise in building scalable, impact-driven technology ventures across emerging markets. Her entrepreneurial journey began with Panda &amp; Wolf Holdings, where she co-founded and scaled two flagship products: Discover Mauritius&trade;, a cultural tourism platform that reached 300,000+ users across iOS, Android and PWA, and Eco-Warriors&trade;, a UNESCO-backed educational gaming platform deployed across 4 African countries.</p>
                <p>Her ventures have collectively reached over 300,000 users, generated $150K+ in revenue, and secured &#8377;2.62 Crore in government funding. Beyond building ventures, she has demonstrated exceptional leadership in securing UNESCO patronage, government contracts, and institutional collaborations across Africa and Asia.</p>
                <p>Prisma Global is the architecture she returned to build &mdash; a multi-venture ecosystem rooted in Solapur, designed to prove that Tier 2 India doesn&rsquo;t need permission to lead.</p>
              </div>
            </Reveal>
            <Reveal delay={0.2} direction="right">
              <ul className="founder-lines">
                <li><span className="founder-dash">&mdash;</span> Founder &amp; CEO, The Prisma Global</li>
                <li><span className="founder-dash">&mdash;</span> Ex-Co-Founder, Panda &amp; Wolf Holdings (Exited 2022)</li>
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 9. JOURNEY */}
      <section className="prisma-journey" data-testid="journey-section">
        <div className="section-inner">
          <Reveal>
            <h2 className="journey-heading">The Journey</h2>
            <p className="journey-sub">Every venture has a before. Here is ours.</p>
          </Reveal>
          <div className="journey-timeline">
            {journey.map((j, i) => (
              <Reveal key={`${j.year}-${j.title}`} delay={i * 0.05}>
                <div className={`journey-entry ${i % 2 === 0 ? 'left' : 'right'}`} data-testid={`journey-entry-${i}`}>
                  <span className="journey-dot" />
                  <div className="journey-content">
                    <span className="journey-year">{j.year}</span>
                    <h3 className="journey-title">{j.title}</h3>
                    <p className="journey-body">{j.body}</p>
                    {j.tags && (
                      <div className="journey-tags" data-testid={`journey-tags-${i}`}>
                        {j.tags.map((t, ti) => <span key={ti} className="journey-tag">{t}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 10. PRISMA STUDIO */}
      <section className="prisma-studio-cta section-light" data-testid="studio-section">
        <div className="section-inner">
          <Reveal>
            <p className="studio-eyebrow">Prisma Studio</p>
            <h2 className="studio-headline">Internal precision, opened selectively.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="studio-body">Prisma Studio is not an agency. It is the execution layer behind every Prisma venture &mdash; now available to a small number of external partners. Branding systems, operational architecture, strategic advisory.</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="studio-action">
              <a className="cta-primary" data-testid="studio-cta-btn" href="mailto:hello@theprismaglobal.com?subject=Prisma%20Studio%20Conversation">Start a Conversation</a>
              <div className="studio-pills">
                {studioServices.map((s) => <span key={s} className="studio-pill" data-testid={`studio-pill-${s.toLowerCase().replace(/\s/g, '-')}`}>{s}</span>)}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 11. IMPACT REPORT CTA */}
      <section className="prisma-impact-cta" id="impact-report-cta" data-testid="impact-cta-section">
        <div className="section-inner impact-cta-grid">
          <Reveal direction="left">
            <div>
              <h2 className="impact-cta-heading">The Prisma Impact Report</h2>
              <p className="impact-cta-sub">A detailed account of what Prisma has built, measured, and proven &mdash; across five ventures, four countries, and one founding vision from Tier 2 India.</p>
              <p className="impact-cta-list-label">What you get:</p>
              <ul className="impact-cta-list">
                <li>Full venture breakdown</li>
                <li>Impact metrics &amp; case data</li>
                <li>Founder&rsquo;s strategic framework</li>
                <li>Roadmap &amp; future architecture</li>
              </ul>
            </div>
          </Reveal>
          <Reveal direction="right" delay={0.15}>
            <div className="impact-cta-buy">
              <span className="impact-cta-price">&#8377;999</span>
              <p className="impact-cta-audience">For investors, collaborators, and founders who want the full picture.</p>
              <div className="impact-cta-button-wrap">
                <button
                  className="cta-primary large impact-disabled-btn"
                  data-testid="get-report-btn"
                  onClick={handleReportClick}
                  aria-describedby="report-tooltip"
                >
                  Get the Report
                </button>
                {tooltipVisible && (
                  <div className="impact-tooltip" id="report-tooltip" role="status" data-testid="report-tooltip">
                    Coming Soon &mdash; Account Activation Pending
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 11.5 INLINE NEWSLETTER */}
      <section className="prisma-newsletter section-light" data-testid="newsletter-section">
        <div className="section-inner newsletter-inner">
          <Reveal>
            <p className="newsletter-eyebrow">Get Prisma Insights</p>
            <p className="newsletter-sub">Occasional drops on systems, ventures, and execution frameworks.</p>
          </Reveal>
          <Reveal delay={0.1}>
            {newsletterDone ? (
              <p className="newsletter-success" data-testid="newsletter-success">You&rsquo;re on the list.</p>
            ) : (
              <form className="newsletter-form" data-testid="newsletter-form" onSubmit={handleNewsletter}>
                <input name="email" type="email" placeholder="your@email.com" required data-testid="newsletter-email" />
                <button type="submit" className="cta-primary" data-testid="newsletter-submit">Join &rarr;</button>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      {/* 12. PRESS HIGHLIGHTS */}
      <section className="prisma-press" data-testid="press-highlights-section">
        <div className="section-inner">
          <Reveal>
            <h2 className="press-heading">Prisma in the Press</h2>
            <p className="press-placeholder" data-testid="press-placeholder">Prisma Global press coverage coming soon.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="press-prior-header" data-testid="press-prior-header">
              <p className="press-prior-label">Founder&rsquo;s Prior Work &mdash; Panda &amp; Wolf Era</p>
              <p className="press-prior-context">Press coverage from Veda Lolge&rsquo;s previous venture, Panda &amp; Wolf Holdings.</p>
            </div>
          </Reveal>
          <div className="press-cards">
            {pressHighlights.map((p, i) => (
              <Reveal key={p.link} delay={i * 0.1} direction="up">
                <a className="press-card" href={p.link} target="_blank" rel="noopener noreferrer" data-testid={`press-card-${i}`}>
                  <span className="press-card-source">{p.source}</span>
                  <h3 className="press-card-headline">{p.headline}</h3>
                  <span className="press-card-cta">Read Article &rarr;</span>
                </a>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div className="press-archive-link-wrap" data-testid="press-archive-link-wrap">
              <button
                className="press-archive-link"
                data-testid="press-archive-btn"
                onClick={() => navigate('/press')}
              >
                See Full Press Archive &rarr;
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 14. CONTACT */}
      <section className="prisma-contact section-light" id="contact" data-testid="contact-section">
        <div className="section-inner contact-grid">
          <Reveal direction="left">
            <div>
              <h2 className="contact-heading">Start a Conversation</h2>
              <p className="contact-sub">We work with a small number of partners, investors, and collaborators at any given time. If the work feels aligned, we want to hear from you.</p>
              <div className="contact-details">
                <p><span className="contact-detail-label">Email</span><a href="mailto:hello@theprismaglobal.com" data-testid="contact-email-link">hello@theprismaglobal.com</a></p>
                <p><span className="contact-detail-label">Based in</span>Solapur, Maharashtra</p>
                <div className="contact-social">
                  <a href="https://www.instagram.com/prisma.global5/" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram">Instagram</a>
                  <span className="contact-social-sep">&middot;</span>
                  <a href="https://www.linkedin.com/in/vedavati-lolge-58412862/" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin">LinkedIn</a>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal direction="right" delay={0.1}>
            {contactDone ? (
              <p className="contact-success" data-testid="contact-success">Thank you. We&rsquo;ll be in touch.</p>
            ) : (
              <form className="contact-form" onSubmit={handleContact} data-testid="contact-form">
                <input name="name" placeholder="Name" required data-testid="contact-name" />
                <input name="email" type="email" placeholder="Email" required data-testid="contact-email" />
                <select name="reason" required defaultValue="" data-testid="contact-reason">
                  <option value="" disabled>Reason</option>
                  <option value="Collaboration">Collaboration</option>
                  <option value="Investment">Investment</option>
                  <option value="Press">Press</option>
                  <option value="Studio">Studio</option>
                  <option value="Other">Other</option>
                </select>
                <textarea name="message" rows="4" placeholder="Message" required data-testid="contact-message" />
                <button type="submit" className="cta-primary" data-testid="contact-submit">Send</button>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
